import React, { useMemo, useState, Fragment, useEffect } from 'react';
import { Outlet, useLocation, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { BsArrowLeft } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { PAGE_PATHS, colors, JOB_STAGE, ROLES } from '../../constants';
import PageHeader from 'components/styled/PageHeader';
import Text from 'components/styled/Text';
import PageHeaderContainer from 'components/styled/PageHeaderContainer';
import { jobSelector, userSelector } from '../../store/selectors';
import { TagStatusFormatter } from '../TagStatusFormatter';
import { useReduxActions } from 'hooks/useReduxActions';
import Avatar from '../Avatar';

const UsersProfileContainer = styled.div`
  display: flex;
  gap: 32px;
`;

const StyledNavContainer = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  padding-left: 32px;
  border-bottom: 1px solid ${colors.grey90};
`;

const Link = styled(Text)`
  min-width: 163.25px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-bottom: ${props =>
    props.color === colors.green && '2px solid' + colors.green};
  cursor: pointer;
`;

const StyledUsersProfile = styled.div`
  display: flex;
  align-items: center;
`;

const StyledUserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AvatarsContainer = styled.div`
  > * {
    margin-left: -18px;
  }
`;

const JobStageNav = () => {
  const location = useLocation();
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const job = useSelector(jobSelector);
  const user = useSelector(userSelector);
  const actions = useReduxActions();
  const [isChosen, setIsChosen] = useState(0);
  const LINKS = [
    {
      id: 0,
      label: t('job.stage.nav.inquiry.link'),
      path: PAGE_PATHS.CREATE_JOB.INQUIRY,
      to: `/${PAGE_PATHS.CREATE_JOB.ROOT}/${PAGE_PATHS.CREATE_JOB.INQUIRY}/${params.ID}`,
      visible: true,
    },
    {
      id: 1,
      label: t('job.stage.nav.search.report.link'),
      path: PAGE_PATHS.CREATE_JOB.SEARCH_REPORT,
      to: `/${PAGE_PATHS.CREATE_JOB.ROOT}/${PAGE_PATHS.CREATE_JOB.SEARCH_REPORT}/${params.ID}`,
      visible:
        job?.stage === JOB_STAGE.SEARCH_REPORT ||
        job?.stage === JOB_STAGE.QUOTE ||
        job?.stage === JOB_STAGE.ORDER,
    },
    {
      id: 2,
      label: t('job.stage.nav.quote.link'),
      path: PAGE_PATHS.CREATE_JOB.QUOTE,
      to: `/${PAGE_PATHS.CREATE_JOB.ROOT}/${PAGE_PATHS.CREATE_JOB.QUOTE}/${params.ID}`,
      visible: job?.stage === JOB_STAGE.QUOTE || job?.stage === JOB_STAGE.ORDER,
    },
    {
      id: 3,
      label: t('job.stage.nav.order.link'),
      path: PAGE_PATHS.CREATE_JOB.ORDER,
      to: `/${PAGE_PATHS.CREATE_JOB.ROOT}/${PAGE_PATHS.CREATE_JOB.ORDER}/${params.ID}`,
      visible: job?.stage === JOB_STAGE.ORDER,
    },
  ];

  useMemo(() => {
    const str = location.pathname.split('/');
    const [link] = LINKS.filter(item => item.path === str[2]);
    setIsChosen(link.id);
  }, [location]); // eslint-disable-line

  const handleClickLink = link => () => {
    setIsChosen(link.id);
    navigate(link.to);
  };

  const redirectToCreateJob = () => {
    navigate(PAGE_PATHS.JOB);
  };

  useEffect(() => {
    return () => actions.resetJob();
  }, []); // eslint-disable-line

  return (
    <>
      <PageHeader size="medium">
        <PageHeaderContainer>
          <BsArrowLeft onClick={redirectToCreateJob} fill={colors.green} />
          <Text variant="body-bold">
            {t('job.stage.nav.title')} {params.ID}
          </Text>
        </PageHeaderContainer>
        {job?.stage === JOB_STAGE.SEARCH_REPORT && user?.role !== ROLES.CLIENT && (
          <UsersProfileContainer>
            <StyledUsersProfile>
              <AvatarsContainer>
                {Object.keys(job.team).map((role, index) => (
                  <Avatar
                    size="medium"
                    key={index}
                    title={`${job.team[role]?.profile?.first_name} ${job.team[role]?.profile?.last_name} (${role})`}
                    image={job.team[role]?.profile?.avatar?.url}
                  />
                ))}
              </AvatarsContainer>
              <StyledUserInfo>
                <Text variant="body-small">{t('user.profile.team')}</Text>
                <Text variant="caption" color={colors.grey40}>
                  {Object.keys(job.team).length} {t('user.profile.people')}
                </Text>
              </StyledUserInfo>
            </StyledUsersProfile>
            <StyledUsersProfile>
              <Avatar size="medium" image={job.user.profile?.avatar?.url} />
              <StyledUserInfo>
                <Text variant="body-small">{`${job.user.profile?.first_name} ${job.user.profile?.last_name}`}</Text>
                <Text variant="caption" color={colors.grey40}>
                  ID: {job.user.ID}
                </Text>
              </StyledUserInfo>
            </StyledUsersProfile>
          </UsersProfileContainer>
        )}
      </PageHeader>
      <StyledNavContainer>
        {LINKS.map(
          link =>
            link.visible && (
              <Fragment key={link.id}>
                <Link
                  color={link.id === isChosen ? colors.green : colors.navy}
                  variant={'body-bold'}
                  onClick={handleClickLink(link)}>
                  {link.label}
                  {link.path === job?.stage && (
                    <TagStatusFormatter status={job?.status[user?.role]} />
                  )}
                </Link>
              </Fragment>
            ),
        )}
      </StyledNavContainer>
      <Outlet />
    </>
  );
};

export default JobStageNav;
